/* eslint-disable no-console */
import { inject, Injectable, signal } from '@angular/core';
import { LoggingService } from '@common/logging/logging.service';

export interface ConsoleLogEntry {
  timestamp: string;
  level: string;
  message: string;
}

const MAX_ENTRIES = 200;
const CAPTURED_LEVELS = ['log', 'warn', 'error', 'info', 'debug'] as const;

@Injectable({ providedIn: 'root' })
export class ConsoleLogService {
  readonly #logger = inject(LoggingService);
  readonly #originals = new Map<string, (...args: unknown[]) => void>();
  readonly enabled = signal(false);

  readonly entries = signal<ConsoleLogEntry[]>([]);

  enable(): void {
    if (this.enabled()) return;

    for (const level of CAPTURED_LEVELS) {
      this.#originals.set(level, console[level].bind(console));

      console[level] = (...args: unknown[]): void => {
        this.#originals.get(level)!(...args);
        this.#push(level, args);
      };
    }

    this.enabled.set(true);
    this.#logger.info(
      `[ConsoleLogService] Enabled — capturing ${CAPTURED_LEVELS.length} levels, buffer max ${MAX_ENTRIES}`
    );
  }

  disable(): void {
    if (!this.enabled()) return;

    for (const level of CAPTURED_LEVELS) {
      const original = this.#originals.get(level);
      if (original) {
        console[level] = original as typeof console.log;
      }
    }
    this.#originals.clear();
    this.enabled.set(false);
    this.#logger.info(
      '[ConsoleLogService] Disabled — restored original console methods'
    );
  }

  clear(): void {
    this.entries.set([]);
  }

  #push(level: string, args: unknown[]): void {
    const entry: ConsoleLogEntry = {
      level: level.toUpperCase(),
      message: args
        .map(a => {
          if (typeof a === 'string') return a;
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(' '),
      timestamp: new Date().toISOString(),
    };

    queueMicrotask(() => {
      this.entries.update(prev => {
        const next = [...prev, entry];
        return next.length > MAX_ENTRIES ? next.slice(-MAX_ENTRIES) : next;
      });
    });
  }
}
